import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFormatters, useI18n } from '../i18n';
import { useAuth } from '../lib/auth';
import { deleteMap } from '../lib/api';
import { describeError } from '../lib/errors';
import { watchSavedMaps, type SavedMap } from '../lib/savedMaps';
import { firebaseConfigured } from '../lib/firebase';
import { ShareToggle } from '../components/ShareToggle';
import { analysisMessageKey } from '../features/analysis/parameters';
import type { AnalysisKind } from '../lib/types';

/** The signed-in user's history, live from Firestore. */
export function SavedMapsRoute() {
  const i18n = useI18n();
  const { t } = i18n;
  const fmt = useFormatters();
  const { status, user, signIn } = useAuth();

  const [maps, setMaps] = useState<SavedMap[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !firebaseConfigured) return;
    setError(null);
    return watchSavedMaps(
      user.uid,
      (next) => {
        setMaps(next);
        setError(null);
      },
      (cause) => setError(t('saved.failed', { message: cause.message })),
    );
  }, [user, t]);

  if (status === 'signed-out' || status === 'unavailable') {
    return (
      <div className="page">
        <div className="page__header">
          <h1 className="page__title">{t('saved.title')}</h1>
        </div>
        <div className="centre-state">
          <p>{t('saved.needAuth')}</p>
          {status === 'signed-out' ? (
            <button type="button" className="button button--primary" onClick={() => void signIn()}>
              {t('header.signIn')}
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  const remove = async (map: SavedMap) => {
    if (!window.confirm(t('saved.deleteConfirm', { name: map.name }))) return;
    setDeleting(map.id);
    try {
      await deleteMap(map.id);
      // The Firestore listener removes the row; nothing to do on success.
    } catch (cause) {
      setError(t('saved.deleteFailed', { message: describeError(cause, i18n).message }));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="page page--wide">
      <div className="page__header">
        <h1 className="page__title">{t('saved.title')}</h1>
        <p className="page__lead">{t('saved.lead')}</p>
      </div>

      {error ? <p className="field__error">{error}</p> : null}

      {maps === null ? (
        <p className="muted">{t('saved.loading')}</p>
      ) : maps.length === 0 ? (
        <div className="centre-state">
          <p>{t('saved.empty')}</p>
          <Link className="button button--primary" to="/">
            {t('nav.new')}
          </Link>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t('saved.col.name')}</th>
                <th>{t('saved.col.analysis')}</th>
                <th>{t('saved.col.size')}</th>
                <th>{t('saved.col.created')}</th>
                <th>{t('saved.col.share')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {maps.map((map) => (
                <tr key={map.id}>
                  <td>
                    <Link className="table__name" to={`/maps/${encodeURIComponent(map.id)}`}>
                      {map.name}
                    </Link>
                  </td>
                  <td className="muted">{t(analysisMessageKey(map.analysis as AnalysisKind))}</td>
                  <td className="table__num">
                    {t('result.meta.items', { n: fmt.int(map.nItems) })}
                    {' · '}
                    {t('result.meta.links', { n: fmt.int(map.nLinks) })}
                  </td>
                  <td className="table__num">
                    {map.createdAt ? fmt.dateTime(map.createdAt) : t('common.dash')}
                  </td>
                  <td>
                    <ShareToggle
                      compact
                      mapId={map.id}
                      shared={map.shared}
                      shareId={map.shareId}
                      onChange={(shared, shareId) =>
                        setMaps((current) =>
                          current
                            ? current.map((row) =>
                                row.id === map.id ? { ...row, shared, shareId } : row,
                              )
                            : current,
                        )
                      }
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="button button--quiet button--danger"
                      disabled={deleting === map.id}
                      onClick={() => void remove(map)}
                    >
                      {t('common.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
